import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ShoppingBag, Tag, ShieldCheck, CalendarDays, AlertTriangle, ArrowRight, MapPin, Clock, CheckCircle2 } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { formatGBP } from "../components/common/StockBadge";
import AgeBadge from "../components/common/AgeBadge";
import api from "../lib/api";

const uuid = () => (crypto?.randomUUID ? crypto.randomUUID() : `idem_${Date.now()}_${Math.random().toString(16).slice(2)}`);
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const mdNum = (d) => (d.getMonth() + 1) * 100 + d.getDate();
const parseMd = (s) => { const [m, dd] = s.split("-"); return parseInt(m, 10) * 100 + parseInt(dd, 10); };
const isValidUkPhone = (v) => /^(?:\+44|0)\d{9,10}$/.test(String(v || "").replace(/[\s()-]/g, ""));

const LEGAL = [
  ["confirm_age", "I confirm that I am aged 18 or over."],
  ["accept_terms", "I accept the Terms & Conditions."],
  ["ack_privacy", "I acknowledge the Privacy Policy."],
  ["ack_challenge25", "I understand that valid ID may be required under Challenge 25."],
];

export default function Checkout() {
  const { user } = useAuth();
  const { refreshBasket } = useShop();
  const navigate = useNavigate();
  const idemKey = useRef(uuid());

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);        // revalidate response
  const [config, setConfig] = useState(null);
  const [ack, setAck] = useState(false);          // price-change acknowledged

  const nameParts = (user?.name || "").trim().split(" ");
  const [firstName, setFirstName] = useState(nameParts[0] || "");
  const [lastName, setLastName] = useState(nameParts.slice(1).join(" ") || "");
  const [phone, setPhone] = useState("");
  const [collectionDate, setCollectionDate] = useState("");
  const [dateError, setDateError] = useState("");
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [touched, setTouched] = useState({});
  const [legal, setLegal] = useState({ confirm_age: false, accept_terms: false, ack_privacy: false, ack_challenge25: false });

  useEffect(() => {
    (async () => {
      try {
        const [rv, cfg, st] = await Promise.all([
          api.post("/checkout/revalidate"), api.get("/checkout/config"), api.get("/checkout/stores"),
        ]);
        setData(rv.data); setConfig(cfg.data);
        const defId = st.data?.default_store_id || "";
        const list = (st.data?.stores || []).slice().sort((a, b) => {
          const ad = a.store_id === defId ? 0 : 1, bd = b.store_id === defId ? 0 : 1;
          if (ad !== bd) return ad - bd;
          const ao = a.display_order ?? 1000, bo = b.display_order ?? 1000;
          if (ao !== bo) return ao - bo;
          return (a.name || "").localeCompare(b.name || "");
        });
        setStores(list);
        // Only auto-select the default store; never auto-select a non-default one.
        setStoreId(defId);
        if (rv.data.empty || (rv.data.items || []).length === 0) { navigate("/basket", { replace: true }); return; }
      } catch { toast.error("Could not load checkout. Please try again."); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const validateDate = (val) => {
    setCollectionDate(val); setDateError("");
  };

  const isDateDisabled = (d) => {
    if (!config) return true;
    const iso = toISO(d);
    if (iso < config.min_date) return true;
    const periods = config.selling_periods || [];
    if (periods.length && !periods.some((p) => mdNum(d) >= parseMd(p.start) && mdNum(d) <= parseMd(p.end))) return true;
    if ((config.blackout_dates || []).includes(iso)) return true;
    const wd = d.getDay();
    const py = wd === 0 ? 6 : wd - 1;
    if (config.allowed_weekdays && !config.allowed_weekdays.includes(py)) return true;
    return false;
  };

  const defaultMonth = useMemo(() => {
    const periods = config?.selling_periods || [];
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let best = null;
    for (const yr of [today.getFullYear(), today.getFullYear() + 1]) {
      for (const p of periods) {
        const [sm, sd] = p.start.split("-");
        const dt = new Date(yr, parseInt(sm, 10) - 1, parseInt(sd, 10));
        if (dt >= today && (!best || dt < best)) best = dt;
      }
    }
    return best || today;
  }, [config]);

  const allLegal = useMemo(() => LEGAL.every(([k]) => legal[k]), [legal]);
  const phoneValid = isValidUkPhone(phone);
  const firstErr = touched.first && !firstName.trim() ? "First name is required." : "";
  const lastErr = touched.last && !lastName.trim() ? "Last name is required." : "";
  const phoneErr = touched.phone && !phone.trim() ? "Phone number is required."
    : touched.phone && !phoneValid ? "Please enter a valid UK phone number." : "";
  const canSubmit = data && !submitting && firstName.trim() && lastName.trim() && phone.trim() && phoneValid
    && storeId && collectionDate && !dateError && allLegal;

  const submit = async () => {
    setTouched({ first: true, last: true, phone: true });
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post("/checkout/submit", {
        idempotency_key: idemKey.current, collection_date: collectionDate, store_id: storeId,
        first_name: firstName.trim(), last_name: lastName.trim(), phone: phone.trim(),
        ...legal, expected_total: data.estimated_total, acknowledged_price_change: ack,
      }).then(async (res) => {
        await refreshBasket();
        navigate(`/reservation/${res.data.reservation_number}`, { replace: true });
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === "price_changed") {
        const rv = await api.post("/checkout/revalidate"); setData(rv.data); setAck(true);
        toast.warning(`Total updated to ${formatGBP(detail.new_total)}. Please review and resubmit.`);
      } else if (detail?.code === "items_unavailable") {
        const rv = await api.post("/checkout/revalidate"); setData(rv.data);
        toast.error(detail.message || "Some items are unavailable. Please amend your basket.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Could not submit reservation. Please try again.");
      }
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen bg-ink grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;
  if (!data) return null;

  const items = data.items || [];
  const discounts = data.discounts || [];
  const promoMessages = data.promotion_messages || [];

  return (
    <div className="min-h-screen bg-ink py-10" data-testid="checkout-page">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <h1 className="font-playfair text-3xl sm:text-4xl text-white flex items-center gap-3"><CalendarDays className="h-7 w-7 text-gold" /> Reservation Checkout</h1>
        <p className="mt-2 text-white/50 text-sm">Reserve online, pay in store on collection. No payment is taken now.</p>

        <div className="mt-8 grid lg:grid-cols-3 gap-8">
          {/* Left: form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Contact */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="checkout-contact">
              <h2 className="font-playfair text-xl text-white">Your details</h2>
              <p className="mt-1 text-xs text-white/50"><span className="text-firered">*</span> indicates a required field</p>
              <div className="mt-4 grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-white/70">First name <span className="text-firered" aria-hidden="true">*</span></Label>
                  <Input data-testid="checkout-first-name" value={firstName} onChange={(e) => setFirstName(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, first: true }))} className="mt-1 bg-black/30 border-white/15 text-white h-11" />
                  {firstErr && <p className="mt-1 text-xs text-firered flex items-center gap-1" data-testid="checkout-first-name-error"><AlertTriangle className="h-3.5 w-3.5" /> {firstErr}</p>}
                </div>
                <div>
                  <Label className="text-white/70">Last name <span className="text-firered" aria-hidden="true">*</span></Label>
                  <Input data-testid="checkout-last-name" value={lastName} onChange={(e) => setLastName(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, last: true }))} className="mt-1 bg-black/30 border-white/15 text-white h-11" />
                  {lastErr && <p className="mt-1 text-xs text-firered flex items-center gap-1" data-testid="checkout-last-name-error"><AlertTriangle className="h-3.5 w-3.5" /> {lastErr}</p>}
                </div>
                <div><Label className="text-white/70">Email</Label><Input value={user?.email || ""} readOnly disabled className="mt-1 bg-black/20 border-white/10 text-white/60 h-11" /></div>
                <div>
                  <Label className="text-white/70">Phone number <span className="text-firered" aria-hidden="true">*</span></Label>
                  <Input data-testid="checkout-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, phone: true }))} placeholder="07123 456789" className={`mt-1 bg-black/30 text-white h-11 ${phoneErr ? "border-firered" : "border-white/15"}`} />
                  {phoneErr && <p className="mt-1 text-xs text-firered flex items-center gap-1" data-testid="checkout-phone-error"><AlertTriangle className="h-3.5 w-3.5" /> {phoneErr}</p>}
                </div>
              </div>
            </section>

            {/* Collection */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="checkout-collection">
              <h2 className="font-playfair text-xl text-white">Collection</h2>
              <p className="mt-1 text-sm text-gold">{config?.location_label}</p>

              <div className="mt-4">
                <Label className="text-white/70">Choose your collection store <span className="text-firered" aria-hidden="true">*</span></Label>
                <div role="radiogroup" aria-label="Collection store" className="mt-2 space-y-3" data-testid="checkout-store-select">
                  {stores.length === 0 && <p className="text-sm text-white/60">No collection store is currently available. Please contact us.</p>}
                  {stores.map((s) => {
                    const selected = s.store_id === storeId;
                    return (
                      <div
                        key={s.store_id}
                        role="radio"
                        aria-checked={selected}
                        tabIndex={0}
                        data-testid={`checkout-store-option-${s.store_id}`}
                        onClick={() => setStoreId(s.store_id)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setStoreId(s.store_id); } }}
                        className={`cursor-pointer rounded-xl border p-4 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-gold ${selected ? "border-gold bg-gold/10" : "border-white/12 bg-black/20 hover:border-white/25"}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="mt-0.5 shrink-0">
                            {selected
                              ? <CheckCircle2 className="h-5 w-5 text-gold" aria-hidden="true" />
                              : <span className="block h-5 w-5 rounded-full border-2 border-white/30" aria-hidden="true" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                              {(s.primary_logo || s.secondary_logo) && (
                                <div className="flex items-center gap-2">
                                  {s.primary_logo && <img src={s.primary_logo} alt={`${s.name} logo`} className="h-9 w-9 rounded-md object-contain bg-white p-0.5 border border-white/10" />}
                                  {s.secondary_logo && <img src={s.secondary_logo} alt={`${s.name} secondary logo`} className="h-9 w-9 rounded-md object-contain bg-white p-0.5 border border-white/10" />}
                                </div>
                              )}
                              <div className="min-w-0">
                                <p className="text-white font-semibold truncate">{s.name}{s.is_default && <span className="ml-2 text-[10px] uppercase tracking-wider text-gold">Default</span>}</p>
                                {s.town && <p className="text-xs text-white/60">{s.town}</p>}
                              </div>
                            </div>
                            {s.full_address && <p className="mt-2 text-sm text-white/75 flex items-start gap-1.5"><MapPin className="h-3.5 w-3.5 text-gold mt-0.5 shrink-0" aria-hidden="true" />{s.full_address}</p>}
                            {s.opening_hours_display?.length > 0 && (
                              <p className="mt-1 text-xs text-white/70 flex items-start gap-1.5"><Clock className="h-3.5 w-3.5 text-gold mt-0.5 shrink-0" aria-hidden="true" /><span>{s.opening_hours_display.join(" · ")}</span></p>
                            )}
                            {s.is_collection_point && (
                              <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" /> Click &amp; Collect available</p>
                            )}
                            {(s.services?.length || s.featured_services?.length) > 0 && (
                              <div className="mt-3">
                                <p className="text-[11px] uppercase tracking-wider text-white/50">Store services</p>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                  {(s.services || s.featured_services || []).map((sv, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-2.5 py-1 text-xs text-white/85 max-w-full">
                                      {sv.image && <img src={sv.image} alt={`${sv.name} logo`} className="h-4 w-4 rounded-sm object-contain bg-white shrink-0" />}
                                      <span className="truncate">{sv.name}</span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {selected && s.collection_instructions && (
                              <p className="mt-2 text-xs text-gold/90" data-testid="checkout-store-detail">{s.collection_instructions}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              {(config?.selling_periods || []).length > 0 && (
                <div className="mt-3 rounded-lg border border-gold/20 bg-gold/5 p-3" data-testid="checkout-periods-note">
                  <p className="text-xs text-white/70">Fireworks may only be reserved and collected during the permitted selling periods:</p>
                  <ul className="mt-1 text-xs text-gold font-medium space-y-0.5">
                    {config.selling_periods.map((p, i) => {
                      const M = ["", "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
                      const f = (md) => { const [m, d] = md.split("-"); return `${parseInt(d, 10)} ${M[parseInt(m, 10)]}`; };
                      return <li key={i}>{f(p.start)} – {f(p.end)}</li>;
                    })}
                  </ul>
                </div>
              )}
              <div className="mt-4 max-w-xs">
                <Label className="text-white/70">Preferred collection date <span className="text-firered" aria-hidden="true">*</span></Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button data-testid="checkout-date" type="button" variant="outline" className="mt-1 w-full justify-start gap-2 bg-black/30 border-white/15 text-white h-11 font-normal hover:bg-black/40 hover:text-white">
                      <CalendarDays className="h-4 w-4 text-gold" />
                      {collectionDate || "Select a collection date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={collectionDate ? new Date(collectionDate + "T00:00:00") : undefined}
                      defaultMonth={defaultMonth}
                      disabled={isDateDisabled}
                      onSelect={(d) => { if (d) validateDate(toISO(d)); }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {dateError && <p className="mt-2 text-xs text-firered flex items-center gap-1" data-testid="checkout-date-error"><AlertTriangle className="h-3.5 w-3.5" /> {dateError}</p>}
                <p className="mt-2 text-xs text-white/40">Only dates within the permitted collection periods can be selected. We'll confirm your collection window when staff review your request.</p>
              </div>
            </section>

            {/* Legal */}
            <section className="rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="checkout-legal">
              <h2 className="font-playfair text-xl text-white">Confirmations</h2>
              <div className="mt-4 space-y-3">
                {LEGAL.map(([k, label]) => (
                  <label key={k} className="flex items-start gap-3 cursor-pointer">
                    <Checkbox data-testid={`legal-${k}`} checked={legal[k]} onCheckedChange={(v) => setLegal((s) => ({ ...s, [k]: !!v }))} className="mt-0.5 border-white/30 data-[state=checked]:bg-gold data-[state=checked]:border-gold" />
                    <span className="text-sm text-white/80">{label}</span>
                  </label>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-3 border-t border-white/10 pt-4" data-testid="checkout-age-notice">
                <AgeBadge size={40} showText={false} linked />
                <p className="text-xs text-white/50 leading-relaxed">
                  Fireworks are sold only to customers aged 18 or over. ID may be required at collection. We reserve the right to refuse sale if valid age verification cannot be provided.
                </p>
              </div>
            </section>
          </div>

          {/* Right: summary */}
          <div className="lg:col-span-1">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6 sticky top-24" data-testid="checkout-summary">
              <h2 className="font-playfair text-xl text-white flex items-center gap-2"><ShoppingBag className="h-5 w-5 text-gold" /> Your reservation</h2>
              <div className="mt-4 space-y-3">
                {items.map((it) => (
                  <div key={it.product_id} className="flex justify-between text-sm" data-testid={`checkout-item-${it.product_id}`}>
                    <span className="text-white/80 min-w-0 truncate">{it.qty} × {it.name}</span>
                    <span className="text-white whitespace-nowrap ml-2">{formatGBP(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-t border-white/10 pt-3 flex justify-between text-white/80 text-sm"><span>Subtotal</span><span data-testid="checkout-subtotal">{formatGBP(data.subtotal)}</span></div>
              {discounts.length > 0 && (
                <div className="mt-2 space-y-1.5" data-testid="checkout-discounts">
                  {discounts.map((d) => (
                    <div key={d.promotion_id} className="flex justify-between text-sm text-emerald-400">
                      <span className="flex items-center gap-1.5 min-w-0" title={d.label}><Tag className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{d.label}{d.is_voucher ? " (coupon)" : ""}</span></span>
                      <span className="whitespace-nowrap">− {formatGBP(d.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-4 border-t border-white/10 pt-4 flex justify-between text-white text-lg font-semibold"><span>Estimated total</span><span data-testid="checkout-estimated-total">{formatGBP(data.estimated_total)}</span></div>
              <p className="mt-1 text-xs text-white/40">Estimated total payable in store. No payment is taken online.</p>

              {promoMessages.filter((m) => m.tone !== "success").length > 0 && (
                <div className="mt-4 space-y-2" data-testid="checkout-promo-messages">
                  {promoMessages.filter((m) => m.tone !== "success").map((m, i) => (
                    <div key={m.promotion_id || i} data-testid={`checkout-promo-message-${i}`} className="rounded-lg border border-gold/30 bg-gold/5 px-3 py-2 text-xs text-gold flex items-start gap-2">
                      <Tag className="h-3.5 w-3.5 shrink-0 mt-0.5" /> <span>{m.message}</span>
                    </div>
                  ))}
                </div>
              )}

              <Button data-testid="submit-reservation-btn" onClick={submit} disabled={!canSubmit} className="cf-gold-btn font-semibold w-full mt-6 gap-2 h-12">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit Reservation Request <ArrowRight className="h-4 w-4" /></>}
              </Button>
              {!allLegal && <p className="mt-3 text-center text-[11px] text-white/40">Please confirm all declarations above to continue.</p>}
              <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-white/40"><ShieldCheck className="h-3.5 w-3.5 text-gold" /> Reserve online, pay in store.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
